// BHP-1062: start / stop / restart against Backend2.0 (BHP-1055).
//
// Delete lives elsewhere: the real destroy endpoint arrived in BHP-1113 and the
// button that drives it in BHP-1116 (VpsDeleteModal), because deleting is not a
// power state — it ends the machine rather than changing how it runs.
//
// BHP-1120: a power action is async at Hetzner — the POST returns while the
// machine is still transitioning. So `run` does not resolve when the request
// returns; it drives the whole lifecycle: fire the action, then poll the
// Hetzner action it hands back (BHP-1119) until a final status, and only then
// refresh the server state. `pendingAction` stays set for that whole window, so
// the buttons stay disabled until Hetzner is actually done — for restart too,
// where the server status alone never shows a transition.
import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { httpClient } from '../../../shared/http/httpClient';
import { toUserMessage } from '../../../shared/http/errorMessage';
import { vpsDetailKeys } from './vpsDetailQueries';

export const POWER_ACTIONS = ['start', 'stop', 'restart'] as const;
export type PowerAction = (typeof POWER_ACTIONS)[number];

/** How often to poll the Hetzner action, and how long before we give up. */
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 3 * 60 * 1000;

interface PowerActionResult {
  vpsUuid: string;
  action: string;
  status: string | null;
  /** BHP-1119: the Hetzner action to poll; null when Hetzner returned none. */
  actionId: number | null;
}

/** BHP-1119: `GET /api/vps/{uuid}/action?id=` — the live status of one action. */
interface VpsActionStatus {
  vpsUuid: string;
  actionId: number;
  command: string | null;
  /** "running" while in flight, then "success" or "error". */
  status: string | null;
  progress: number | null;
}

const sleep = async (ms: number): Promise<void> =>
  await new Promise((resolve) => setTimeout(resolve, ms));

type Outcome = 'success' | 'error' | 'timeout';

/** The only status that means "keep waiting" — Hetzner's in-flight state. */
const IN_FLIGHT = 'running';

/**
 * Poll one Hetzner action until it is no longer running, or the deadline
 * passes. Tracking the action (not the server status) is what makes this work
 * for restart, and what surfaces a failed action instead of a silent no-op.
 *
 * Only an explicit "running" keeps us polling; every other value — "success",
 * "error", or anything unexpected — is terminal. Treating the unknown as done
 * (rather than as "keep going") is deliberate: the action either finished or is
 * no longer trackable, so blocking the buttons until the 3-minute timeout would
 * be worse than releasing them. Only a literal "error" is reported as a failure.
 */
async function pollActionToFinish(
  uuid: string,
  actionId: number,
  deadline: number
): Promise<Outcome> {
  for (;;) {
    const action = await httpClient.get<VpsActionStatus>(
      `/vps/${uuid}/action?id=${actionId}`,
      { accept: 'application/json' }
    );

    if (action.status !== IN_FLIGHT) {
      return action.status === 'error' ? 'error' : 'success';
    }
    if (Date.now() >= deadline) {
      return 'timeout';
    }

    await sleep(POLL_INTERVAL_MS);
  }
}

export function useVpsPowerAction(uuid: string): {
  run: (action: PowerAction) => void;
  isBusy: boolean;
  pendingAction: PowerAction | null;
  error: string | null;
  clearError: () => void;
} {
  const queryClient = useQueryClient();
  const [pendingAction, setPendingAction] = useState<PowerAction | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(
    (action: PowerAction): void => {
      // Buttons are disabled while busy, but guard anyway: never run two at once.
      if (pendingAction !== null) {
        return;
      }
      setError(null);
      setPendingAction(action);

      void (async () => {
        try {
          const result = await httpClient.post<PowerActionResult>(
            `/vps/${uuid}/${action}`,
            undefined,
            { accept: 'application/json' }
          );

          // No action to follow (Hetzner returned none): treat the POST as the
          // whole story rather than poll something that does not exist.
          if (result.actionId !== null) {
            const outcome = await pollActionToFinish(
              uuid,
              result.actionId,
              Date.now() + POLL_TIMEOUT_MS
            );
            if (outcome === 'error') {
              setError(
                `Could not ${action} this server — Hetzner reported it failed.`
              );
            } else if (outcome === 'timeout') {
              setError(
                'This is taking longer than expected — refresh to see the latest status.'
              );
            }
          }
        } catch (caught) {
          setError(toUserMessage(caught, `Could not ${action} this server.`));
        } finally {
          // Refresh the live state regardless of outcome, then release the
          // buttons. Awaited so a running/off badge is not a frame behind.
          // (React 18 makes a state update after unmount a safe no-op, so no
          // mounted-ref guard — that guard misfired under StrictMode's
          // setup/cleanup/setup and left the buttons stuck.)
          await queryClient.invalidateQueries({
            queryKey: vpsDetailKeys.state(uuid)
          });
          setPendingAction(null);
        }
      })();
    },
    [uuid, queryClient, pendingAction]
  );

  return {
    run,
    isBusy: pendingAction !== null,
    pendingAction,
    error,
    clearError: () => {
      setError(null);
    }
  };
}
